// Reusable React components shared across Google Ads panels.

import React from 'react';
import { DeltaBadge } from '../../shared/DeltaBadge';
import { INSIGHT_MIN_CLICKS, INSIGHT_MIN_CONVERSIONS, LOW_DATA_MSG } from './constants';
import { normalizeInsight } from './format';

/* ----------------------------- DeltaBadge -----------------------------
 * Lifted to the cross-feature src/components/shared/DeltaBadge (it was identical
 * to the GA4 copy). Re-exported here so existing Google Ads callers
 * (`import { DeltaBadge } from "../shared"`) keep working.
 */
export { DeltaBadge };

/* ----------------------------- KpiCard -----------------------------
 * Lifted to the cross-feature src/components/shared/KpiCard (it was structurally
 * identical to the GA4 copy; the shared version adopts GA4's responsive variants as
 * the baseline and the union of both icon sets). Re-exported here so existing Google
 * Ads callers (`import { KpiCard } from "../shared"`) keep working.
 */
export { KpiCard } from '../../shared/KpiCard';

/* ----------------------------- ExportMenu -----------------------------
 * Lifted to the cross-feature src/components/shared/ExportMenu. Re-exported here
 * so existing Google Ads callers (`import { ExportMenu } from "../shared"`) keep working.
 */
export { ExportMenu } from '../../shared/ExportMenu';

/* ----------------------------- badges -----------------------------
   These return JSX so they belong with components, not format helpers.
*/
export const campStatusBadge = (status) => {
  const isActive = status === 'ENABLED';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[12px] font-semibold uppercase ${
      isActive
        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
    }`}>
      {isActive ? 'Active' : 'Paused'}
    </span>
  );
};

export const campTypeBadge = (type) => {
  const styles = {
    SEARCH: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    PERFORMANCE_MAX: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    DISPLAY: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    SHOPPING: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
    VIDEO: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[12px] font-semibold uppercase ${styles[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
      {(type || 'unknown').replace(/_/g, ' ')}
    </span>
  );
};

export const matchTypeBadge = (matchType) => {
  const styles = {
    EXACT: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    NEAR_EXACT: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
    PHRASE: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
    BROAD: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    AI_MAX: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[12px] font-semibold uppercase ${styles[matchType] || styles.BROAD}`}>
      {(matchType || 'unknown').replace(/_/g, ' ')}
    </span>
  );
};

/* ----------------------------- DimInsightCard ----------------------------- */
export const DimInsightCard = ({ insight, totalClicks, totalConv, customParts }) => {
  if (totalClicks < INSIGHT_MIN_CLICKS) {
    return (
      <div className="mt-4 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{LOW_DATA_MSG}</p>
      </div>
    );
  }
  if (insight) {
    return (
      <div className="mt-4 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
          <span className="font-semibold">Insight:</span> {normalizeInsight(insight)}
          {totalConv < INSIGHT_MIN_CONVERSIONS && (
            <span className="ml-1 text-gray-400">(Note: conversion-based insights may be unreliable — only {totalConv} conversion{totalConv !== 1 ? 's' : ''} in this period)</span>
          )}
        </p>
      </div>
    );
  }
  if (customParts?.parts?.length) {
    const convWarning = totalConv < INSIGHT_MIN_CONVERSIONS ? ` (Note: only ${totalConv} conversion${totalConv !== 1 ? 's' : ''} — ${customParts.warningSuffix || 'conversion insights omitted'})` : '';
    return (
      <div className="mt-4 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
          <span className="font-semibold">Insight:</span> {customParts.parts.join('. ')}.{convWarning && <span className="text-gray-400">{convWarning}</span>}
        </p>
      </div>
    );
  }
  return null;
};
